import React, { FunctionComponent, MouseEvent } from 'react';
import { useHistory } from 'react-router';
import { TableRowData, TableColumn, AnalyticsProps } from '@inplayer-org/inplayer-ui';
import styled from 'styled-components';

// types
import { AccessFee, CurrentPhase } from 'ducks/merchantDucks/items/types';
import { ItemType } from 'ducks/merchantDucks/items/actions';

// utils
import { convertArrayToCamelCase } from 'utils/helpers';

// Components
import GenericTable from 'components/Table/GenericTable';
import CommonIcon from 'components/CommonIcon';
import Rules from 'restrictions/roles/constants/rules';
import RestrictedByRule from 'restrictions/roles/components/RestrictedByRule';
import { accessTypeNames } from 'components/PaywallPreviews/PaywallElements/PriceCard';

const StyledSpan = styled.span<AnalyticsProps>`
  vertical-align: text-top;
`;

export const Wrapper = styled.div`
  > * {
    grid-auto-rows: initial;
    grid-gap: initial;
    padding-top: 2px;
    padding-bottom: 4px;
  }
`;

interface Price {
  id: number;
  merchant_id: number;
  amount: number;
  currency: string;
  description: string;

  access_type: {
    id: number;
    name: string;
    quantity: number;
    period: string;
  };
  trial_period: {
    quantity: number;
    period: string;
    description: string;
  };
  setup_fee: {
    fee_amount: number;
    description: string;
  };
  expires_at: number;
  current_phase: CurrentPhase;
}

interface PriceTableData extends TableRowData {
  accessType: {
    id: number;
    name: string;
    quantity: number;
    currency: string;
    period: string;
  };
  amount: number;
  currency: string;
  description: string;
  currentPhase: CurrentPhase;
}

interface OwnProps extends AnalyticsProps {
  prices: Array<AccessFee>;
  type?: string;
  selectAccessFee?: (id: number) => void;
  isPackageSection?: boolean;
  handleShowModal?: (itemId: number, feeId: number, startsAt: number, expiresAt: number, isSeasonFee: boolean) => void;
  showRowActions?: boolean;
}

type Props = OwnProps;

const IconName: any = {
  asset: 'edit',
  package: 'arrowRight',
};

const PricesTable: FunctionComponent<Props> = ({
  prices,
  type = '',
  selectAccessFee,
  isPackageSection,
  handleShowModal,
  showRowActions = true,
}) => {
  const history = useHistory();
  const pricesCollection = convertArrayToCamelCase<Price, PriceTableData>(prices);

  const generateColumns = (): Array<TableColumn<PriceTableData>> => [
    {
      title: 'Price description',
      key: 'description',
    },
    {
      title: 'Amount',
      key: '',
      render: ({ rowValues }) => {
        const {
          amount,
          currency,
          accessType: { name },
          currentPhase,
        } = rowValues;
        let priceAmount = amount;
        let priceCurrency = currency;
        const isSeasonAccessFee = name === accessTypeNames.SEASON;

        if (isSeasonAccessFee && currentPhase) {
          const { current_price: currentPrice, currency: currentCurrency } = currentPhase;
          priceAmount = currentPrice;
          priceCurrency = currentCurrency;
        }

        return <>{`${priceAmount} ${priceCurrency}`}</>;
      },
    },
    {
      title: 'Payment type',
      key: '',
      render: ({ rowValues }) => {
        const {
          accessType: { quantity, period, name },
        } = rowValues;

        let accessType = `${quantity} ${period} ${name}`;
        if (period === 'custom') accessType = 'ppv custom';
        if (name === 'season') accessType = 'season';

        return <>{`${accessType}`}</>;
      },
    },
  ];

  const handleEditPrice = ({ row }: any) => {
    if (type === ItemType.Asset) {
      const { id } = row;
      if (selectAccessFee) {
        selectAccessFee(id);
      }
      return;
    }
    const {
      item: { id: packageId },
    } = row;
    history.replace(`/assets/packages/edit/${packageId}`);
  };

  if (isPackageSection) {
    // TODO: fix this error
    // eslint-disable-next-line no-param-reassign
    type = ItemType.Asset;
  }

  const renderRowActions = (): any => [
    {
      render: ({ row }: any) => (
        <StyledSpan key={`edit-${row.id + 1}`}>
          <CommonIcon
            tag={`button_edit_${row.id}`}
            name={`${IconName[type]}`}
            hoverStatus={`${IconName[type]}`}
            onClick={(e) => {
              e.preventDefault();
              handleEditPrice({ row });
            }}
          />
        </StyledSpan>
      ),
    },
    {
      render: ({ row: { item, id: priceId, templateId, startsAt, expiresAt, accessType } }: any) => {
        const isAssetPrice = item?.item_type?.name !== ItemType.Package || isPackageSection;

        const isSeasonFee = accessType?.name === 'season';

        const itemId = templateId ?? item.id;

        const showModal = (e: MouseEvent<HTMLElement>) => {
          e.preventDefault();
          if (handleShowModal) {
            handleShowModal(itemId, priceId, startsAt, expiresAt, isSeasonFee);
          }
        };
        return (
          isAssetPrice && (
            <RestrictedByRule rule={Rules.READONLY_ASSETS_AND_PACKAGES} key={`delete-${priceId}`}>
              <StyledSpan>
                <CommonIcon tag={`button_delete_${itemId}`} name="delete" hoverStatus="delete" onClick={showModal} />
              </StyledSpan>
            </RestrictedByRule>
          )
        );
      },
    },
  ];

  return (
    <Wrapper>
      <GenericTable<PriceTableData>
        columns={generateColumns()}
        data={pricesCollection}
        showLoader={false}
        options={{
          rowActions: showRowActions && renderRowActions(),
        }}
        type="prices"
        actionsRowTitle="Edit"
      />
    </Wrapper>
  );
};

export default PricesTable;
